import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="territory-selector"
export default class extends Controller {
  static targets = ["searchInput", "dropdown", "results", "selectedContainer"]
  static values = {
    type: String,  // "included" or "excluded"
    territories: Array,
    fieldId: String,  // ID of the hidden field to update
    territoryMap: Object  // Territory code => name mapping
  }

  connect() {
    this.selectedTerritories = new Set()

    // Territory mapping: code => name (from Ruby helper via data attribute)
    this.territoryMap = this.territoryMapValue

    // Load existing territories from hidden field
    this.loadExistingTerritories()

    // Bind the click outside handler and add listener
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener('click', this.handleOutsideClick)
  }

  loadExistingTerritories() {
    const hiddenField = document.getElementById(this.fieldIdValue)
    if (hiddenField && hiddenField.value) {
      try {
        const territoryCodes = JSON.parse(hiddenField.value)
        if (Array.isArray(territoryCodes)) {
          territoryCodes.forEach(code => {
            this.selectedTerritories.add(code)
            this.addTerritoryTag(code)
          })
        }
      } catch (e) {
        console.error("Failed to load existing territories:", e)
      }
    }
  }

  updateHiddenField() {
    const hiddenField = document.getElementById(this.fieldIdValue)
    if (hiddenField) {
      hiddenField.value = JSON.stringify(Array.from(this.selectedTerritories))
    }
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim()

    const filtered = Object.entries(this.territoryMap).filter(([code, name]) => {
      // Exclude "Worldwide" from excluded territories dropdown
      if (this.typeValue === 'excluded' && code === 'WO') {
        return false
      }
      return (query.length === 0 || name.toLowerCase().includes(query)) &&
             !this.selectedTerritories.has(code)
    })

    if (filtered.length > 0) {
      this.showResults(filtered)
      this.showDropdown()
    } else {
      this.hideDropdown()
    }
  }

  focusSearch(event) {
    // Show all available territories when focusing on search
    const available = Object.entries(this.territoryMap).filter(([code, name]) => {
      // Exclude "Worldwide" from excluded territories dropdown
      if (this.typeValue === 'excluded' && code === 'WO') {
        return false
      }
      return !this.selectedTerritories.has(code)
    })

    if (available.length > 0) {
      this.showResults(available)
      this.showDropdown()
    }
  }

  showResults(territories) {
    if (territories.length === 0) {
      this.resultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">No territories found</div>'
      return
    }

    // Sort territories with "Worldwide" first, then alphabetically by name
    const sorted = territories.sort(([codeA, nameA], [codeB, nameB]) => {
      if (codeA === "WO") return -1
      if (codeB === "WO") return 1
      return nameA.localeCompare(nameB)
    })

    this.resultsTarget.innerHTML = sorted
      .map(([code, name]) => `
        <div class="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-900"
             data-action="click->territory-selector#selectTerritory"
             data-territory-code="${code}">
          ${name}
        </div>
      `).join('')
  }

  selectTerritory(event) {
    const code = event.currentTarget.dataset.territoryCode

    if (!this.selectedTerritories.has(code)) {
      this.selectedTerritories.add(code)
      this.addTerritoryTag(code)
      this.searchInputTarget.value = ''
      this.hideDropdown()
      this.updateHiddenField()
    }
  }

  addTerritoryTag(code) {
    const name = this.territoryMap[code] || code

    const colorClass = this.typeValue === 'included'
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200'

    const buttonColorClass = this.typeValue === 'included'
      ? 'text-green-600 hover:text-green-800'
      : 'text-red-600 hover:text-red-800'

    const tag = document.createElement('span')
    tag.className = `inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border ${colorClass}`
    tag.innerHTML = `
      ${name}
      <button type="button"
              class="ml-1.5 inline-flex items-center justify-center ${buttonColorClass} cursor-pointer"
              data-action="click->territory-selector#removeTerritory"
              data-territory-code="${code}">
        <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
        </svg>
      </button>
    `
    this.selectedContainerTarget.appendChild(tag)
  }

  removeTerritory(event) {
    event.preventDefault()
    const code = event.currentTarget.dataset.territoryCode

    if (this.selectedTerritories.has(code)) {
      this.selectedTerritories.delete(code)
      event.currentTarget.closest('span').remove()
      this.updateHiddenField()
    }

    return false
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden')
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
  }

  // Close dropdown when clicking outside
  handleOutsideClick(event) {
    // Check if click is outside this controller's element
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
  }
}
